import { Loader } from "../Loader";
import type React from "react";
import { type FunctionComponent, Fragment, type CSSProperties } from "react";
import { ButtonLabel } from "./ButtonLabel";
import { StyledButton } from "./StyledButton";
import * as ICONS from "../Icons";
import type { Size, Variant } from "./types";

export declare type ButtonProps = {
	onClick?: () => void;
	label: string | React.ReactNode;
	inner?: FunctionComponent<any>;
	innerProps?: Record<string, any>;
	size?: Size;
	variant?: Variant;
	disabled?: boolean;
	icon?: keyof typeof ICONS;
	iconPosition?: "start" | "end";
	iconProps?: any;
	/** indicate a background process is running by rendering a spinner */
	busy?: boolean;
	mt?: string;
	mb?: string;
	ml?: string;
	mr?: string;
	className?: string;
	width?: string;
	maxWidth?: string;
	flexible?: boolean;
	style?: CSSProperties;
	id?: string;
	type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
};

export const Button: FunctionComponent<ButtonProps> = ({
	label,
	onClick = () => {},
	inner,
	icon,
	innerProps = {},
	size = "md",
	variant = "primary",
	disabled = false,
	mt = "0",
	mb = "0",
	ml = "0",
	mr = "0",
	className = "",
	width,
	maxWidth,
	busy = false,
	iconPosition = "start",
	iconProps = {},
	flexible,
	style = {},
	type = "button",
	...props
}) => {
	const Inner = inner || ButtonLabel;

	const IconComponent = ((icon && ICONS[icon]) ||
		Fragment) as FunctionComponent;

	return (
		<StyledButton
			onClick={!disabled ? onClick : undefined}
			$size={size}
			$variant={variant}
			disabled={disabled}
			$busy={busy}
			$mt={mt}
			$mb={mb}
			$ml={ml}
			$mr={mr}
			$width={width}
			$maxWidth={maxWidth}
			$flexible={flexible}
			style={style}
			className={className}
			type={type}
			{...props}
		>
			{busy ? (
				<Loader type="ellipsis" />
			) : (
				<>
					{icon && iconPosition === "start" && <IconComponent {...iconProps} />}
					<Inner
						label={label}
						size={size}
						disabled={disabled}
						{...innerProps}
					/>
					{icon && iconPosition === "end" && <IconComponent {...iconProps} />}
				</>
			)}
		</StyledButton>
	);
};
